import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { logDebug, logError, showToast } from '../../utils';
import CancelConfirmDialog from '../../components/cancel-confirm-dialog';
import {
  Order,
  ORDER_SCHEMAS,
  OrderSchemaKey,
  OrderDetailsFormData,
  OrderShippingFormData,
  OrderLoadRatingFormData,
  getBaseSchemaKeys,
} from '../../types';
import { hasValidatedJobLocation, jobLocationToShippingAddress } from './job-location-shipping';

// Union type for all possible form data types
export type OrderFormData = OrderDetailsFormData | OrderShippingFormData | OrderLoadRatingFormData;

interface OrderUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  title: string;
  schemaKey: OrderSchemaKey;
  onSubmit: (data: OrderFormData) => Promise<void>;
  children: React.ReactNode;
  isSubmitting?: boolean;
}

const OrderUpdateDialog: React.FC<OrderUpdateDialogProps> = ({
  open,
  onClose,
  order,
  title,
  schemaKey,
  onSubmit,
  children,
  isSubmitting = false,
}) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Get the schema and create default values
  const schema = ORDER_SCHEMAS[schemaKey];

  const defaultValues = useMemo((): FieldValues => {
    const schemaKeys = getBaseSchemaKeys(schemaKey);
    const values: FieldValues = {};

    schemaKeys.forEach(key => {
      values[key] = (order as unknown as Record<string, unknown>)[key];
    });

    if (schemaKey === 'shipping') {
      const pickup = values.pickup;
      let shippingAddress = values.shippingAddress;

      if (!pickup && !shippingAddress?.address1 && hasValidatedJobLocation(order.jobLocation)) {
        shippingAddress = jobLocationToShippingAddress(order.jobLocation);
        values.shippingAddress = shippingAddress;
      }

      values.useBillingAddress = !pickup && !shippingAddress?.address1;
      values.stateConfirmed = true;
    }

    return values;
  }, [schemaKey, order]);

  const methods = useForm<FieldValues>({
    // @ts-expect-error TS2769
    resolver: zodResolver(schema as z.ZodSchema<FieldValues>),
    defaultValues,
    mode: 'onTouched',
  });

  const { reset, formState, handleSubmit } = methods;

  const handleCancel = () => {
    if (formState.isDirty) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    handleClose();
  };

  const handleCancelDismiss = () => {
    setCancelDialogOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmitHandler = handleSubmit(async (data: FieldValues) => {
    try {
      await onSubmit(data as OrderFormData);
      showToast('Order updated successfully', 'success');
      handleClose();
    } catch (error) {
      logError('Error updating order:', error);
      showToast('Failed to update order. Please try again.', 'error');
    }
  });

  useEffect(() => {
    logDebug('Form validation state:', {
      isValid: formState.isValid,
      isDirty: formState.isDirty,
      errors: formState.errors,
      touchedFields: formState.touchedFields,
      dirtyFields: formState.dirtyFields,
    });
  }, [formState]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, defaultValues, reset]);

  const defaultWidth = isMediumScreen ? '90%' : '80%';
  const dialogWidth = isSmallScreen ? '100%' : defaultWidth;

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby="update-order-dialog-title"
        PaperProps={{
          sx: {
            width: dialogWidth,
            maxWidth: theme.breakpoints.values.md,
            minHeight: isSmallScreen ? '100%' : '75vh',
            maxHeight: isSmallScreen ? '100%' : '80vh',
            display: 'flex',
            flexDirection: 'column',
            m: isSmallScreen ? 0 : 2,
          },
        }}
      >
        <DialogTitle id="update-order-dialog-title" sx={{ pb: 0 }}>
          Order {order.orderNumber}
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            flex: 1,
            overflow: 'auto',
            p: theme.spacing(3),
            minHeight: isSmallScreen ? 'auto' : '50vh',
          }}
        >
          {/* Section title with vertical bar */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.primary,
                fontWeight: 600,
                '&::before': {
                  content: '""',
                  width: '4px',
                  height: '24px',
                  backgroundColor: theme.palette.primary.main,
                  marginRight: theme.spacing(2),
                  borderRadius: '2px',
                },
              }}
            >
              {title}
            </Typography>
          </Box>

          <FormProvider {...methods}>
            <form onSubmit={onSubmitHandler}>
              <Box sx={{ mt: 2 }}>{children}</Box>
            </form>
          </FormProvider>
        </DialogContent>

        <DialogActions sx={{ p: theme.spacing(2, 3) }}>
          <Button onClick={handleCancel} color="error" disabled={isSubmitting}>
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            onClick={onSubmitHandler}
            variant="contained"
            color="primary"
            disabled={!formState.isDirty || !formState.isValid || isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onDismiss={handleCancelDismiss}
        dialogTitle="Cancel Updates?"
      />
    </>
  );
};

export default OrderUpdateDialog;
